import {
  DirectMessageAreaKey,
  IReceivedDirectMessageForArea,
  ISentDirectMessage,
} from 'providence-types';
import { DirectMessageActions } from '@store/actions';
import { getIdParamFromUrl } from '../../utils/methods/get-id-param-from-url';
import { StoredRequest } from '../../utils/store/stored-request-decorator';
import { CommonHttpService } from './common-http.service';

export class DirectMessageHttpService extends CommonHttpService {
  @StoredRequest(DirectMessageActions.getDirectMessages)
  getDirectMessages(
    areaKey: DirectMessageAreaKey,
  ): Promise<ISentDirectMessage[]> {
    const [projectId] = getIdParamFromUrl();

    return this.http.callApi.get(`${this.apiUrl}/direct-messages`, null, {
      params: { areaKey, projectId },
    });
  }

  @StoredRequest(DirectMessageActions.getReceivedDirectMessages)
  getReceivedDirectMessages(
    areaKey: DirectMessageAreaKey,
  ): Promise<IReceivedDirectMessageForArea[]> {
    const [projectId] = getIdParamFromUrl();

    return this.http.callApi.get(
      `${this.apiUrl}/direct-messages/received`,
      null,
      {
        params: { areaKey, projectId },
      },
    );
  }

  @StoredRequest(DirectMessageActions.getUsersByArea)
  getUsersByArea(areaKey: DirectMessageAreaKey): Promise<ISentDirectMessage[]> {
    const [projectId] = getIdParamFromUrl();

    return this.http.callApi.get(`${this.apiUrl}/direct-messages/users`, null, {
      params: { areaKey, projectId },
    });
  }

  @StoredRequest(DirectMessageActions.createDirectMessage)
  async createDirectMessages(
    body: Pick<ISentDirectMessage, 'receivers' | 'message'>,
    areaKey: DirectMessageAreaKey,
  ): Promise<ISentDirectMessage> {
    const [projectId] = getIdParamFromUrl();

    const _id = await this.http.callApi.post(`${this.apiUrl}/direct-messages`, {
      ...body,
      areaKey,
      projectId,
    });

    return {
      _id,
      ...body,
      completed: false,
    };
  }

  @StoredRequest(DirectMessageActions.updateDirectMessage)
  async updateDirectMessages(
    { completed, _id, ...body }: ISentDirectMessage,
    areaKey: string,
  ): Promise<Omit<ISentDirectMessage, 'completed'>> {
    const [projectId] = getIdParamFromUrl();

    await this.http.callApi.patch(
      `${this.apiUrl}/direct-messages`,
      { ...body, areaKey, projectId },
      {
        params: { _id },
      },
    );

    return { _id, ...body };
  }

  @StoredRequest(DirectMessageActions.setCompletedDirectMessage)
  async setCompletedDirectMessage(_id: string): Promise<string> {
    await this.http.callApi.patch(
      `${this.apiUrl}/direct-messages/completed`,
      null,
      { params: { _id } },
    );

    return _id;
  }

  @StoredRequest(DirectMessageActions.multiSetCompletedDirectMessage)
  async multiSetCompletedDirectMessage(recordIds: string[]): Promise<string[]> {
    await this.http.callApi.patch(
      `${this.apiUrl}/direct-messages/multi-completed`,
      null,
      { params: { recordIds } },
    );

    return recordIds;
  }

  @StoredRequest(DirectMessageActions.removeDirectMessage)
  async removeDirectMessages(_id: string): Promise<string> {
    await this.http.callApi.delete(`${this.apiUrl}/direct-messages`, null, {
      params: { _id },
    });

    return _id;
  }
}
